import 'react-responsive-carousel/lib/styles/carousel.min.css'

import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import createBreakpoints from '@material-ui/core/styles/createBreakpoints'
import React, { FC, useEffect, useState } from 'react'
import { Carousel } from 'react-responsive-carousel'

import { rspTheme } from '../../styles/rspTheme'
import {
  defaultInitialPardotFormValues,
  FORM_LOCALSTORAGE_KEY,
  getBusinessEmailFromLocalStorage,
  LocalStorageFormDataType,
  useAskGDPRPermission,
  useForm,
} from '../../utils/form/useForm'
import { cleanString, jsonParseLocalStorage, useServerSideSafeValue } from '../../utils/helpers'
import { useHandleSubmitWithRecaptcha } from '../../utils/recaptcha'
import GoogleAnalyticsHiddenFields from '../GoogleAnalyticsHiddenFields'
import Wrapper from '../UI/Wrapper'
import DotIndicators from './DotIndicators'
import StepFour from './StepFour'
import StepOne from './StepOne'
import StepThree from './StepThree'
import StepTwo from './StepTwo'
import { StepFormSection } from './styles'

const breakpoints = createBreakpoints({})

const theme = createTheme({
  ...rspTheme,
  palette: {
    ...rspTheme.palette,
    primary: {
      main: '#004D6B',
    },
  },
  overrides: {
    MuiTextField: {
      root: {
        marginTop: 28,
        borderRadius: 4,
        backgroundColor: '#ffffff',
        boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.08)',
      },
    },
    MuiFormLabel: {
      root: {
        color: rspTheme.palette.text.hint,
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: 'transparent',
      },
      input: {
        padding: '28px 12px 22px',
      },
      underline: {
        '&::before': {
          borderBottomColor: 'transparent',
        },
      },
      multiline: {
        minHeight: 140,
        alignItems: 'flex-start',
      },
    },
    MuiSelect: {
      select: {
        padding: '23px 12px 8px',
      },
    },
    MuiInputLabel: {
      filled: {
        '&$shrink': { transform: 'translate(12px, 6px) scale(0.75)' },
      },
    },
    MuiFormControl: {
      root: {
        position: 'relative',
      },
    },
    MuiFormControlLabel: {
      root: {
        alignItems: 'flex-start',
      },
      label: {
        textAlign: 'left',
      },
    },
    MuiFormHelperText: {
      root: {
        position: 'absolute',
        top: '100%',
      },
      contained: {
        marginLeft: 0,
        width: '100%',
      },
    },
    MuiButton: {
      contained: {
        fontSize: 18,
        padding: '4px 16px',
        borderRadius: 6,
        [breakpoints.up('sm')]: {
          minWidth: 140,
        },
      },
    },
  },
})

interface StepFormProps {
  formData: any
}

const StepForm: FC<StepFormProps> = ({ formData }) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const handleNext = () => {
    if (currentSlide < 3) {
      setCurrentSlide(currentSlide + 1)
    }
  }

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1)
    }
  }

  const { value: localStorageFormData, isReady: isReady_1 } = useServerSideSafeValue(() =>
    jsonParseLocalStorage<LocalStorageFormDataType>(localStorage?.getItem(FORM_LOCALSTORAGE_KEY)),
  )
  const { value: businessEmail, isReady: isReady_2 } = useServerSideSafeValue(() =>
    getBusinessEmailFromLocalStorage(),
  )
  const prefilledInitialValues = businessEmail ? localStorageFormData?.[businessEmail] : null

  const formId = 'multiStepContactForm'

  const form = useForm({
    formId,
    endpoint: 'want-more-info',
    errors: formData,
    initialValues: prefilledInitialValues ? prefilledInitialValues : defaultInitialPardotFormValues,
    onSuccess: () => setCurrentSlide(3),
    emailField: 'businessEmail',
  })

  const handleSubmit = useHandleSubmitWithRecaptcha(form.handleSubmit, form.isValid)

  useEffect(() => {
    if (isReady_1 && isReady_2) {
      form.setValues(
        prefilledInitialValues ? prefilledInitialValues : defaultInitialPardotFormValues,
      )
    }
  }, [isReady_1, isReady_2])

  const shouldAskGDPRPermission = useAskGDPRPermission(
    formData,
    form?.values?.country,
    form?.values?.GDPRConsent,
  )

  return (
    <ThemeProvider theme={theme}>
      <StepFormSection>
        <Wrapper>
          <form id={formId} onSubmit={handleSubmit}>
            <GoogleAnalyticsHiddenFields />
            <Carousel
              centerMode={true}
              centerSlidePercentage={100}
              selectedItem={currentSlide}
              showArrows={false}
              showIndicators={false}
              showStatus={false}
              showThumbs={false}
              swipeable={false}
            >
              <StepOne
                body={formData.body}
                currentSlide={currentSlide}
                firstNameLabel={cleanString(formData.first_name_label[0].copy)}
                form={form}
                handleNext={handleNext}
                headline={formData.headline}
                lastNameLabel={cleanString(formData.last_name_label[0].copy)}
              />
              <StepTwo
                businessEmailLabel={cleanString(formData.business_email_label[0].copy)}
                companyNameLabel={cleanString(formData.company_name_label[0].copy)}
                countries={formData.countries?.[0]?.reference}
                countriesLabel={cleanString(formData.countries_label[0].copy)}
                currentSlide={currentSlide}
                form={form}
                handleNext={handleNext}
                handlePrev={handlePrev}
                headline={formData.headline}
                jobTitleLabel={cleanString(formData.job_title_label[0].copy)}
                numberOfSellersLabel={cleanString(formData.number_of_sellers_label[0].copy)}
                phoneNumberLabel={cleanString(formData.phone_number_label[0].copy)}
              />
              <StepThree
                GDPRCountryPermission={shouldAskGDPRPermission}
                currentSlide={currentSlide}
                form={form}
                gdprLabel={formData?.gdpr_consent_language}
                handlePrev={handlePrev}
                headline={formData.headline}
                submitText={formData.submit_text}
                trainingInterestsLabel={cleanString(formData.interests_label[0].copy)}
              />
              <StepFour successMessage={formData.success_message} />
            </Carousel>
          </form>
          <DotIndicators currentSlide={currentSlide} numberOfDots={4} />
        </Wrapper>
      </StepFormSection>
    </ThemeProvider>
  )
}

export default StepForm
